import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
  Typography,
  Box
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import EmojiObjectsIcon from "@material-ui/icons/EmojiObjects";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import KeyboardReturnIcon from "@material-ui/icons/KeyboardReturn";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  trigger: {
    color: theme.palette.type === "dark" ? "#ffd54f" : "#f9a825"
  },
  suggestionBox: {
    background:
      theme.palette.type === "dark"
        ? "rgba(255, 213, 79, 0.08)"
        : "rgba(249, 168, 37, 0.08)",
    border: `1px solid ${
      theme.palette.type === "dark" ? "#ffd54f" : "#f9a825"
    }`,
    borderRadius: 8,
    padding: theme.spacing(2),
    fontSize: 15,
    lineHeight: 1.55,
    whiteSpace: "pre-wrap",
    fontFamily: "system-ui, -apple-system, sans-serif",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2)
  },
  meta: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  rationale: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1)
  },
  internalNote: {
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    background:
      theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "#f5f5f5",
    borderLeft: `3px solid #9e9e9e`,
    borderRadius: 4,
    fontSize: 13,
    fontStyle: "italic"
  },
  noteLabel: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: theme.palette.text.secondary,
    marginBottom: 4
  },
  banner: {
    background: "rgba(198,40,40,0.10)",
    border: "1px solid #c62828",
    borderRadius: 6,
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
    fontSize: 13,
    color: theme.palette.type === "dark" ? "#ff8a80" : "#b71c1c"
  },
  warn: {
    background: "rgba(239,108,0,0.10)",
    border: "1px solid #ef6c00",
    borderRadius: 6,
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
    fontSize: 12
  },
  nextAction: {
    marginTop: theme.spacing(1),
    fontSize: 13
  },
  contextBox: {
    marginTop: theme.spacing(0.5),
    padding: theme.spacing(1),
    background:
      theme.palette.type === "dark" ? "rgba(255,255,255,0.03)" : "#fafafa",
    border: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255,255,255,0.10)" : "#e0e0e0"
    }`,
    borderRadius: 6,
    maxHeight: 200,
    overflowY: "auto"
  },
  contextItem: {
    fontSize: 12,
    lineHeight: 1.5,
    padding: "2px 0",
    borderBottom: `1px dashed ${
      theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#eee"
    }`
  },
  audioOk: {
    background: "rgba(46,125,50,0.10)",
    border: "1px solid #2e7d32",
    borderRadius: 6,
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
    fontSize: 12,
    color: theme.palette.type === "dark" ? "#a5d6a7" : "#1b5e20"
  },
  audioTranscribed: {
    background:
      theme.palette.type === "dark"
        ? "rgba(129,199,132,0.14)"
        : "rgba(46,125,50,0.10)",
    borderRadius: 4,
    padding: "1px 4px"
  },
  audioPending: {
    color: theme.palette.text.secondary,
    fontStyle: "italic"
  }
}));

// Espelha o contrato do backend (Fase B3): em contextPreview, itens de áudio têm
// type "áudio" e text = transcrição OU o literal "(áudio não transcrito)".
const AUDIO_NOT_TRANSCRIBED = "(áudio não transcrito)";

const isAudioPreviewItem = m => {
  const t = (m && m.type ? m.type : "").toString().toLowerCase();
  return t.includes("áudio") || t.includes("audio");
};

const isTranscribedAudioItem = m => {
  if (!isAudioPreviewItem(m)) return false;
  const text = (m && m.text ? m.text : "").toString().trim();
  return text.length > 0 && text !== AUDIO_NOT_TRANSCRIBED;
};

const urgencyColor = u => {
  switch ((u || "").toLowerCase()) {
    case "urgente":
      return { backgroundColor: "#b71c1c", color: "white" };
    case "alta":
      return { backgroundColor: "#c62828", color: "white" };
    case "media":
      return { backgroundColor: "#ef6c00", color: "white" };
    case "baixa":
      return { backgroundColor: "#2e7d32", color: "white" };
    default:
      return {};
  }
};

const formatMediaSummary = ms => {
  if (!ms) return "";
  const parts = [];
  if (ms.audio) parts.push(`${ms.audio} áudio${ms.audio > 1 ? "s" : ""}`);
  if (ms.image) parts.push(`${ms.image} imagem${ms.image > 1 ? "ns" : ""}`);
  if (ms.document)
    parts.push(`${ms.document} documento${ms.document > 1 ? "s" : ""}`);
  if (ms.video) parts.push(`${ms.video} vídeo${ms.video > 1 ? "s" : ""}`);
  if (ms.other) parts.push(`${ms.other} outro${ms.other > 1 ? "s" : ""}`);
  return parts.join(", ");
};

const SuggestReplyButton = ({ ticketId, disabled }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showContext, setShowContext] = useState(false);

  const fetchSuggestion = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post(
        "/api/integrations/ai/suggest-reply",
        { ticketId }
      );
      setResult(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = async () => {
    if (!ticketId) {
      toast.warn("Selecione um ticket antes de pedir sugestão.");
      return;
    }
    setOpen(true);
    await fetchSuggestion();
  };

  const handleClose = () => setOpen(false);

  const handleCopy = () => {
    if (!result?.suggestion) return;
    try {
      navigator.clipboard.writeText(result.suggestion);
      toast.success("Sugestão copiada");
    } catch {
      toast.warn("Não foi possível copiar automaticamente.");
    }
  };

  const handleUseInInput = () => {
    if (!result?.suggestion) return;
    // Dispatch custom event que o MessageInput escuta.
    window.dispatchEvent(
      new CustomEvent("mitchat:prefill-message", {
        detail: { text: result.suggestion, ticketId }
      })
    );
    toast.success("Texto preenchido no campo de mensagem (revise antes de enviar)");
    setOpen(false);
  };

  // Estado de áudio derivado do contrato do backend. Tudo tolerante a resposta
  // antiga/ausente: se os campos não existirem, nada quebra e nada é exibido.
  const previewItems = Array.isArray(result?.contextPreview)
    ? result.contextPreview
    : [];
  const transcribedAudioCount = previewItems.filter(isTranscribedAudioItem).length;
  const hasPendingAudio = !!(result && result.hasAudioWithoutTranscript);

  return (
    <>
      <Tooltip title="Sugerir resposta">
        <span>
          <IconButton
            onClick={handleOpen}
            disabled={disabled}
            size="small"
            className={classes.trigger}
            aria-label="Sugerir resposta"
          >
            <EmojiObjectsIcon />
          </IconButton>
        </span>
      </Tooltip>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        aria-labelledby="suggest-dialog-title"
      >
        <DialogTitle id="suggest-dialog-title">
          ✨ Sugerir resposta
        </DialogTitle>
        <DialogContent dividers>
          {loading && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={28} />
            </Box>
          )}
          {!loading && result && (
            <>
              <Box className={classes.meta}>
                <Chip
                  size="small"
                  label={`Fonte: ${result.source === "ai" ? "IA" : "Heurístico"}`}
                  color={result.source === "ai" ? "primary" : "default"}
                />
                {result.category && (
                  <Chip
                    size="small"
                    label={`Categoria: ${result.category}`}
                    variant="outlined"
                  />
                )}
                {result.urgency && (
                  <Chip
                    size="small"
                    label={`Urgência: ${result.urgency}`}
                    style={urgencyColor(result.urgency)}
                  />
                )}
                <Chip
                  size="small"
                  label={`${result.contextMessages || 0} msg de contexto`}
                  variant="outlined"
                />
              </Box>

              {result.insufficientContext && (
                <div className={classes.banner}>
                  ⚠️ A IA indicou <strong>contexto insuficiente</strong> para
                  uma resposta segura. Colete mais informações com o cliente
                  antes de responder.
                </div>
              )}

              {transcribedAudioCount > 0 && (
                <div className={classes.audioOk}>
                  🎧{" "}
                  <strong>
                    {transcribedAudioCount} áudio(s) transcrito(s)
                  </strong>{" "}
                  considerado(s) pela IA nesta análise.
                </div>
              )}

              {hasPendingAudio && (
                <div className={classes.warn}>
                  🎧 {transcribedAudioCount > 0 ? "Ainda há" : "Há"} áudio(s) no
                  contexto <strong>não transcrito(s)</strong>. A IA não
                  considerou o conteúdo desse(s) áudio(s).
                  {transcribedAudioCount > 0 &&
                    " Alguns podem não ter sido transcritos por falha ou por limite de processamento."}
                </div>
              )}

              {formatMediaSummary(result.mediaSummary) && (
                <Typography
                  variant="caption"
                  color="textSecondary"
                  style={{ display: "block", marginBottom: 6 }}
                >
                  Contexto contém: {formatMediaSummary(result.mediaSummary)}.
                </Typography>
              )}

              {result.lastCustomerMessage && (
                <Typography
                  variant="caption"
                  color="textSecondary"
                  style={{ display: "block", marginBottom: 6 }}
                >
                  <strong>Última mensagem do cliente considerada:</strong> "
                  {result.lastCustomerMessage}"
                </Typography>
              )}

              <Typography variant="caption" color="textSecondary">
                Texto sugerido (revise antes de enviar):
              </Typography>
              <div className={classes.suggestionBox}>{result.suggestion}</div>

              {result.rationale && (
                <Typography className={classes.rationale}>
                  <strong>Por quê:</strong> {result.rationale}
                </Typography>
              )}

              {result.internalNote && (
                <div className={classes.internalNote}>
                  <div className={classes.noteLabel}>
                    Nota interna (não vai ao cliente)
                  </div>
                  {result.internalNote}
                </div>
              )}

              {result.nextAction && (
                <Typography className={classes.nextAction}>
                  <strong>Próxima ação:</strong> {result.nextAction}
                </Typography>
              )}

              {Array.isArray(result.contextPreview) &&
                result.contextPreview.length > 0 && (
                  <Box mt={1.5}>
                    <Button
                      size="small"
                      onClick={() => setShowContext(v => !v)}
                      color="default"
                      style={{ textTransform: "none", padding: 0 }}
                    >
                      {showContext ? "▼" : "▶"} Contexto analisado pela IA (
                      {result.contextPreview.length})
                    </Button>
                    {showContext && (
                      <div className={classes.contextBox}>
                        {result.contextPreview.slice(0, 8).map((m, i) => {
                          const audio = isAudioPreviewItem(m);
                          const transcribed = isTranscribedAudioItem(m);
                          return (
                            <div key={i} className={classes.contextItem}>
                              <strong>
                                [{m.who} · {m.type} · {m.time}]
                              </strong>{" "}
                              {audio ? (
                                transcribed ? (
                                  <span className={classes.audioTranscribed}>
                                    🎧 Transcrição: {m.text}
                                  </span>
                                ) : (
                                  <span className={classes.audioPending}>
                                    🎧 {m.text}
                                  </span>
                                )
                              ) : (
                                m.text
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </Box>
                )}

              <Typography
                variant="caption"
                color="textSecondary"
                style={{ display: "block", marginTop: 12 }}
              >
                ⚠️ Sugestão <strong>não é enviada automaticamente</strong>.
                Use "Usar no campo" para preencher o input, revise e envie
                manualmente.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="default">
            Fechar
          </Button>
          <Button
            onClick={fetchSuggestion}
            disabled={loading}
            color="default"
          >
            Gerar outra
          </Button>
          <Button
            onClick={handleCopy}
            disabled={loading || !result?.suggestion}
            color="default"
            startIcon={<FileCopyOutlinedIcon />}
          >
            Copiar
          </Button>
          <Button
            onClick={handleUseInInput}
            disabled={loading || !result?.suggestion}
            color="primary"
            variant="contained"
            startIcon={<KeyboardReturnIcon />}
          >
            Usar no campo
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SuggestReplyButton;
